/**
 * @file Checklist Form Component
 * @description Form for downloading the checklist PDF
 * 
 * Fields:
 * - Nome (text)
 * - Email (email, required)
 * - Quanti brand gestisci? (dropdown: 1-2 / 3-5 / 6-10 / 10+)
 * - Newsletter opt-in (checkbox, pre-selected)
 */

"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createChecklistLead } from "@/lib/actions/checklist-leads";
import { 
  Loader2, 
  CheckCircle2, 
  AlertTriangle,
  User,
  Mail,
  Building2,
  ArrowRight
} from "lucide-react";
import { useRouter } from "next/navigation";

// Validation schema
const checklistFormSchema = z.object({
  name: z.string().min(2, "Nome troppo corto"),
  email: z.string().email("Email non valida"),
  brandCount: z.enum(["1-2", "3-5", "6-10", "10+"]).refine((val) => val !== undefined, {
    message: "Seleziona quanti brand gestisci",
  }),
  newsletterOptIn: z.boolean().default(true),
});

type ChecklistFormData = z.infer<typeof checklistFormSchema>;

interface ChecklistFormProps {
  utmParams: {
    source: string;
    medium: string;
    campaign: string;
  };
}

export function ChecklistForm({ utmParams }: ChecklistFormProps) {
  const router = useRouter();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<ChecklistFormData>({
    resolver: zodResolver(checklistFormSchema),
    defaultValues: {
      name: "",
      email: "",
      brandCount: undefined,
      newsletterOptIn: true,
    },
  });

  async function onSubmit(data: ChecklistFormData) {
    setErrorMessage(null);
    setIsDuplicate(false);

    try {
      const result = await createChecklistLead({
        name: data.name,
        email: data.email,
        brandCount: data.brandCount,
        newsletterOptIn: data.newsletterOptIn,
        utmSource: utmParams.source,
        utmMedium: utmParams.medium,
        utmCampaign: utmParams.campaign,
      });

      if (result.success) {
        setIsSuccess(true);
        setIsDuplicate(result.isDuplicate || false);
        
        // Redirect to thank you page after a short delay
        setTimeout(() => {
          router.push("/checklist-sopravvissuto/grazie");
        }, 1500);
      } else {
        setErrorMessage(result.error || "Si è verificato un errore imprevisto.");
      }
    } catch (error) {
      setErrorMessage("Si è verificato un errore di connessione. Riprova.");
    }
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="text-center py-8" role="status" aria-live="polite">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 ${isDuplicate ? 'bg-blue-50' : 'bg-emerald-100'}`}>
          <CheckCircle2 className={`w-8 h-8 ${isDuplicate ? 'text-blue-600' : 'text-emerald-600'}`} aria-hidden="true" />
        </div>
        
        <h3 className="text-xl font-bold text-mocha-900 mb-3">
          {isDuplicate ? "Email già registrata!" : "Grazie!"}
        </h3>
        
        <p className="text-mocha-600 mb-4">
          {isDuplicate 
            ? "Controlla la tua email per scaricare di nuovo la checklist." 
            : "Controlla la tua email per scaricare la checklist."}
        </p>
        
        <p className="text-sm text-mocha-500">
          Reindirizzamento in corso...
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      {/* Error Message */}
      {errorMessage && (
        <div 
          className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" 
          role="alert"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Name Field */}
      <div className="space-y-1.5">
        <Label htmlFor="name" className="text-sm font-medium text-mocha-700">
          Nome <span className="text-red-500" aria-hidden="true">*</span>
        </Label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mocha-400" aria-hidden="true" />
          <Input
            id="name"
            type="text"
            placeholder="Mario Rossi"
            {...register("name")}
            className={`pl-10 ${errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-ruby-200'}`}
            aria-invalid={errors.name ? 'true' : 'false'}
          />
        </div>
        {errors.name && (
          <p className="text-xs text-red-600 flex items-center gap-1" role="alert">
            <AlertTriangle className="w-3 h-3" aria-hidden="true" />
            {errors.name.message}
          </p>
        )}
      </div>

      {/* Email Field */}
      <div className="space-y-1.5">
        <Label htmlFor="email" className="text-sm font-medium text-mocha-700">
          Email <span className="text-red-500" aria-hidden="true">*</span>
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mocha-400" aria-hidden="true" />
          <Input
            id="email"
            type="email"
            placeholder="mario@esempio.com"
            {...register("email")}
            className={`pl-10 ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-ruby-200'}`}
            aria-invalid={errors.email ? 'true' : 'false'}
            autoComplete="email"
          />
        </div>
        {errors.email && (
          <p className="text-xs text-red-600 flex items-center gap-1" role="alert">
            <AlertTriangle className="w-3 h-3" aria-hidden="true" />
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Brand Count Dropdown */}
      <div className="space-y-1.5">
        <Label htmlFor="brandCount" className="text-sm font-medium text-mocha-700">
          Quanti brand gestisci? <span className="text-red-500" aria-hidden="true">*</span>
        </Label>
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mocha-400" aria-hidden="true" />
          <select
            id="brandCount"
            {...register("brandCount")}
            className={`w-full pl-10 pr-4 py-2.5 rounded-md border text-sm bg-white ${
              errors.brandCount 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-200' 
                : 'border-ruby-200 focus:border-honey-500 focus:ring-honey-200'
            } focus:outline-none focus:ring-2 transition-colors appearance-none`}
            aria-invalid={errors.brandCount ? 'true' : 'false'}
          >
            <option value="">Seleziona...</option>
            <option value="1-2">1-2 brand</option>
            <option value="3-5">3-5 brand</option>
            <option value="6-10">6-10 brand</option>
            <option value="10+">10+ brand</option>
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg className="w-4 h-4 text-mocha-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {errors.brandCount && (
          <p className="text-xs text-red-600 flex items-center gap-1" role="alert">
            <AlertTriangle className="w-3 h-3" aria-hidden="true" />
            {errors.brandCount.message}
          </p>
        )}
      </div>

      {/* Newsletter Opt-in */}
      <div className="flex items-start gap-3 p-4 bg-honey-50 rounded-lg border border-ruby-100">
        <input
          type="checkbox"
          id="newsletterOptIn"
          {...register("newsletterOptIn")}
          className="w-4 h-4 mt-0.5 rounded border-ruby-300 text-honey-600 focus:ring-honey-500"
        />
        <Label htmlFor="newsletterOptIn" className="text-sm text-mocha-600 leading-relaxed cursor-pointer">
          <strong>Ricevi anche: La newsletter "La Cassaforte"</strong> — 1 strategia/settimana per agenti plurimandatari (annulla quando vuoi)
        </Label>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-honey-600 hover:bg-honey-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed h-auto"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin mr-2" aria-hidden="true" />
            Invio in corso...
          </>
        ) : (
          <>
            Scarica Gratis il PDF
            <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
          </>
        )}
      </Button>

      {/* Trust Microcopy */}
      <p className="text-xs text-center text-mocha-500">
        Cliccando, accetti la nostra <Link href="/privacy" className="underline hover:text-mocha-900">Privacy Policy</Link>.
        <br />
        Nessun dato sarà condiviso con terzi.
      </p>
    </form>
  );
}

// Need to import Link for the privacy policy link
import Link from "next/link";
